import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Images, CalendarBlank, MapPin, SpinnerGap, CheckCircle, Check, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";

// Post-import review: photos auto-grouped by month & place, with keep/skip → one-tap publish.
export default function ImportedReview() {
  const nav = useNavigate();
  const { state } = useLocation();
  const ids = state?.ids || [];
  const [data, setData] = useState(undefined);
  const [kept, setKept] = useState(null); // Set of group keys to publish (default: all)
  const [publishing, setPublishing] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    if (!ids.length) { nav("/library"); return; }
    let stop = false;
    const run = async () => {
      try {
        const { data } = await api.post("/photos/group-summary", { ids });
        if (stop) return;
        setData(data);
        setKept((prev) => prev || new Set(data.groups.map((g) => g.key))); // default keep all
        polls.current += 1;
        if (data.processing > 0 && polls.current < 6) setTimeout(run, 3000);
      } catch { if (!stop) setData(null); }
    };
    run();
    return () => { stop = true; };
  }, []); // eslint-disable-line

  const toggle = (k) => setKept((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const keptIds = () => (data?.groups || []).filter((g) => kept?.has(g.key)).flatMap((g) => g.photo_ids);
  const keptCount = keptIds().length;

  const publishKept = async () => {
    const list = keptIds();
    if (!list.length) { toast.error("Keep at least one group to publish"); return; }
    setPublishing(true);
    try {
      const { data } = await api.post("/photos/publish-set", { ids: list });
      toast.success(`Published ${data.published} photo${data.published === 1 ? "" : "s"}`);
      nav("/library");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not publish"); }
    setPublishing(false);
  };

  return (
    <div className="mx-auto max-w-5xl pb-24">
      <div className="flex items-center gap-2 text-[#2D6A4F]"><CheckCircle size={22} weight="fill" /><p className="overline text-[10px]">Imported</p></div>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Grouped by month &amp; place</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Your {ids.length} imported photo{ids.length === 1 ? "" : "s"} are in your Library as private drafts. Keep the sets worth sharing and publish them in one tap — the rest stay private.
      </p>

      {data === undefined && <div className="mt-16 grid place-items-center"><SpinnerGap size={32} className="animate-spin text-[#2D6A4F]" /></div>}
      {data === null && <p className="mt-10 text-sm text-muted-foreground">Could not load the summary. <Link to="/library" className="font-bold underline">Go to Library</Link></p>}

      {data && (
        <>
          {data.processing > 0 && (
            <p className="mt-4 flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700" data-testid="imported-processing">
              <SpinnerGap size={14} className="animate-spin" /> Still reading dates &amp; places for {data.processing} photo{data.processing === 1 ? "" : "s"}…
            </p>
          )}
          <div className="mt-6 space-y-7" data-testid="imported-groups">
            {data.groups.map((g) => {
              const on = kept?.has(g.key);
              return (
                <div key={g.key} data-testid={`imported-group-${g.key}`} className={on ? "" : "opacity-45"}>
                  <button onClick={() => toggle(g.key)} data-testid={`imported-toggle-${g.key}`} className="mb-2 flex items-center gap-2">
                    <span className={`grid h-5 w-5 place-items-center rounded-md border ${on ? "border-[#2D6A4F] bg-[#2D6A4F] text-white" : "border-zinc-300"}`}>{on && <Check size={13} weight="bold" />}</span>
                    <CalendarBlank size={16} weight="fill" className="text-[#F27A54]" />
                    <span className="font-display text-base font-bold">{g.label}</span>
                    {g.place && <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground"><MapPin size={12} weight="fill" /> {g.place}</span>}
                    <span className="text-xs text-muted-foreground">· {g.count}</span>
                    <span className="ml-1 text-[11px] font-bold" style={{ color: on ? "#2D6A4F" : "#a1a1aa" }}>{on ? "Keep" : "Skip"}</span>
                  </button>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {g.photo_ids.slice(0, 8).map((pid) => (
                      <Link key={pid} to={`/p/${pid}`} className="aspect-square overflow-hidden rounded-lg">
                        <img src={imageUrl(pid, "display")} alt="" loading="lazy" className="h-full w-full object-cover transition-transform hover:scale-105" />
                      </Link>
                    ))}
                    {g.count > 8 && <div className="grid aspect-square place-items-center rounded-lg bg-zinc-100 text-xs font-bold text-zinc-500">+{g.count - 8}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {data && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E5DFD3] bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-3">
            <p className="text-sm font-bold" data-testid="imported-keep-count">{keptCount} photo{keptCount === 1 ? "" : "s"} to publish · rest stay private</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => nav("/library")} className="h-10 rounded-full">Review in Library</Button>
              <Button data-testid="imported-publish" onClick={publishKept} disabled={publishing || !keptCount}
                className="h-10 gap-1.5 rounded-full bg-[#2D6A4F] px-5 text-white hover:bg-[#245840] disabled:opacity-50">
                {publishing ? <SpinnerGap size={16} className="animate-spin" /> : <><Globe size={15} weight="fill" /> Publish {keptCount || ""}</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
